import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useRememberedState, useRememberedScroll } from '../../hooks/useRemembered';
import {
    Lead,
    LeadActivity,
    createLead,
    fetchActiveProjectPoints,
    fetchLeads,
    listOffeneWiedervorlagen,
    setActivityDone,
    setLeadStatus,
} from '../../services/leadService';
import { ActiveProjectPoint, LeadStatus } from '../../services/leadLogic';
import { rechtFehlerOder } from '../../services/rechte/rechtFehler';
import { useVertriebRechte } from './useVertriebRechte';
import { Button } from '../Button';
import { LeadsBoard } from './LeadsBoard';
import { LeadsListView } from './LeadsListView';
import { LeadDetailPanel } from './LeadDetailPanel';
import { LeadMatchReviewModal } from './LeadMatchReviewModal';
import { LeadImportDialog } from './LeadImportDialog';
import {
    AlertTriangle, Ban, CalendarClock, Check, DownloadCloud, GitMerge, KanbanSquare, List, Loader2,
    Plus, RefreshCw, Target, X,
} from 'lucide-react';

/**
 * Container der Lead-Pipeline: laedt Daten, haelt den Ansichtswechsel
 * (Board/Liste) und verdrahtet Detail, Review und Statuswechsel.
 *
 * Bewusst eine EIGENE Datei statt eines weiteren Abschnitts in CrmView: die
 * Kundenansicht ist mit 600 Zeilen bereits gross genug, und die Standing
 * Decision verlangt duenne Container. CrmView rendert nur noch den Reiter.
 */
interface Props {
    /** Faellige Wiedervorlagen fuer das Reiter-Abzeichen melden. */
    onDueCountChange?: (n: number) => void;
}

const heuteIso = (): string => new Date().toLocaleDateString('sv-SE');

const datumDe = (iso?: string): string => (iso ? new Date(iso).toLocaleDateString('de-DE') : '');

export const LeadsView: React.FC<Props> = ({ onDueCountChange }) => {
    // Rollensystem: die Ansicht gibt „Neukunden / Leads“ frei; das Umwandeln
    // in einen Kunden braucht zusaetzlich „Kunden verwalten“.
    const darf = useVertriebRechte();
    const [leads, setLeads] = useState<Lead[]>([]);
    const [activeProjects, setActiveProjects] = useState<ActiveProjectPoint[]>([]);
    const [faellige, setFaellige] = useState<LeadActivity[]>([]);
    const [laedt, setLaedt] = useState(true);
    const [fehler, setFehler] = useState<string | null>(null);

    const [ansicht, setAnsicht] = useRememberedState<'board' | 'liste'>('leads.ansicht', 'board');
    const scrollRef = useRememberedScroll(`leads.${ansicht}.scroll`);
    const [ausgewaehlt, setAusgewaehlt] = useState<Lead | null>(null);
    const [reviewOffen, setReviewOffen] = useState(false);
    const [importOffen, setImportOffen] = useState(false);

    // Neuanlage
    const [anlegenOffen, setAnlegenOffen] = useState(false);
    const [neuerName, setNeuerName] = useState('');
    const [lege, setLege] = useState(false);

    // Verloren-Grund aus dem Board (im Detail hat der Dialog seinen eigenen Weg)
    const [verlorenFuer, setVerlorenFuer] = useState<Lead | null>(null);
    const [grund, setGrund] = useState('');

    const laden = useCallback(async () => {
        setLaedt(true);
        setFehler(null);
        try {
            // Drei unabhaengige Abfragen parallel — nacheinander waere die
            // Ansicht dreimal so lange leer.
            const [leadRows, projekte, offene] = await Promise.all([
                fetchLeads(),
                // Die Bezugspunkte der Priorisierung sind Beiwerk: faellt der
                // Abruf aus, bleibt der Score allein — die Pipeline muss
                // deswegen nicht ungenutzt bleiben.
                fetchActiveProjectPoints().catch(() => [] as ActiveProjectPoint[]),
                listOffeneWiedervorlagen(heuteIso()).catch(() => [] as LeadActivity[]),
            ]);
            setLeads(leadRows);
            setActiveProjects(projekte);
            setFaellige(offene);
            onDueCountChange?.(offene.length);
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setLaedt(false);
        }
    }, [onDueCountChange]);

    useEffect(() => {
        void laden();
    }, [laden]);

    /** Einen geaenderten Lead in Liste und Auswahl nachziehen. */
    const uebernehmen = (aktualisiert: Lead) => {
        setLeads((prev) => prev.map((l) => (l.id === aktualisiert.id ? aktualisiert : l)));
        setAusgewaehlt((prev) => (prev?.id === aktualisiert.id ? aktualisiert : prev));
    };

    const statusWechseln = async (lead: Lead, to: LeadStatus) => {
        // „Verloren" ohne Grund gibt es nicht — sonst ist die Absage spaeter
        // nicht auswertbar (dieselbe Regel wie in leadService.setLeadStatus).
        if (to === 'verloren' && !(lead.lostReason || '').trim()) {
            setVerlorenFuer(lead);
            setGrund('');
            return;
        }
        setFehler(null);
        try {
            uebernehmen(await setLeadStatus(lead.id, to, { lostReason: lead.lostReason }));
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        }
    };

    const verlorenBestaetigen = async () => {
        const lead = verlorenFuer;
        const text = grund.trim();
        if (!lead || !text) return;
        setVerlorenFuer(null);
        setFehler(null);
        try {
            uebernehmen(await setLeadStatus(lead.id, 'verloren', { lostReason: text }));
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        }
    };

    const anlegen = async () => {
        const name = neuerName.trim();
        if (!name) return;
        setLege(true);
        setFehler(null);
        try {
            const neu = await createLead({ name, quelle: 'manuell' });
            setLeads((prev) => [neu, ...prev]);
            setAnlegenOffen(false);
            setNeuerName('');
            setAusgewaehlt(neu);
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        } finally {
            setLege(false);
        }
    };

    const wiedervorlageErledigen = async (a: LeadActivity) => {
        try {
            await setActivityDone(a.id, true);
            const rest = faellige.filter((x) => x.id !== a.id);
            setFaellige(rest);
            onDueCountChange?.(rest.length);
        } catch (e) {
            setFehler(rechtFehlerOder(e));
        }
    };

    const offeneAnzahl = useMemo(
        () => leads.filter((l) => !['gewonnen', 'verloren', 'gesperrt'].includes(l.status)).length,
        [leads],
    );

    // ─── Detailansicht ───────────────────────────────────────────────────────
    if (ausgewaehlt) {
        return (
            <div data-glass-view="leads" className="h-full overflow-y-auto">
                <div className="max-w-7xl mx-auto p-4 md:p-6 pb-20 animate-fade-in">
                    <LeadDetailPanel
                        lead={ausgewaehlt}
                        activeProjects={activeProjects}
                        onBack={() => setAusgewaehlt(null)}
                        onChange={uebernehmen}
                        onStatusChange={(l, to) => void statusWechseln(l, to)}
                        darfKundeAnlegen={darf.kunden}
                    />
                </div>
            </div>
        );
    }

    return (
        <div ref={scrollRef} data-glass-view="leads" className="h-full overflow-y-auto">
            <div className="space-y-5 max-w-7xl mx-auto animate-fade-in p-4 md:p-6 pb-20">
                {/* Kopf */}
                <div data-glass="kopf" className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 flex flex-col lg:flex-row justify-between items-center gap-4">
                    <div className="flex items-center gap-4 w-full lg:w-auto">
                        <div className="p-3 bg-indigo-50 text-indigo-600 rounded-2xl">
                            <Target className="w-7 h-7" />
                        </div>
                        <div>
                            <h2 className="text-xl font-bold text-slate-800">Leads</h2>
                            <p className="text-xs text-slate-500 font-medium uppercase tracking-wider">
                                {offeneAnzahl} offen · {leads.length} gesamt
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2 justify-end">
                        <div data-glass="umschalter" className="inline-flex rounded-xl border border-slate-200 overflow-hidden">
                            {([
                                { key: 'board' as const, label: 'Board', icon: <KanbanSquare className="w-4 h-4" /> },
                                { key: 'liste' as const, label: 'Liste', icon: <List className="w-4 h-4" /> },
                            ]).map((a) => (
                                <button
                                    key={a.key}
                                    type="button"
                                    onClick={() => setAnsicht(a.key)}
                                    aria-pressed={ansicht === a.key}
                                    className={`flex items-center gap-2 px-4 py-2 text-sm font-bold transition-all ${
                                        ansicht === a.key
                                            ? 'bg-blue-50 text-blue-600'
                                            : 'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
                                    }`}
                                >
                                    {a.icon}{a.label}
                                </button>
                            ))}
                        </div>
                        <Button variant="secondary" onClick={() => void laden()} icon={<RefreshCw className="w-4 h-4" />}>
                            Aktualisieren
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={() => setImportOffen(true)}
                            icon={<DownloadCloud className="w-4 h-4" />}
                            title="Neue Funde des Lead-Scouts als Leads übernehmen und abgleichen"
                        >
                            Neue Treffer importieren
                        </Button>
                        <Button
                            variant="secondary"
                            onClick={() => setReviewOffen(true)}
                            icon={<GitMerge className="w-4 h-4" />}
                            title="Gefundene Firmen prüfen, bei denen die Zuordnung unsicher war"
                        >
                            Zuordnungen prüfen
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() => { setNeuerName(''); setAnlegenOffen(true); }}
                            icon={<Plus className="w-4 h-4" />}
                            className="shadow-lg border-b-2 border-blue-700"
                        >
                            Neuer Lead
                        </Button>
                    </div>
                </div>

                {fehler && (
                    <div className="bg-red-50 border border-red-200 text-red-800 rounded-2xl p-4 text-sm flex items-start justify-between gap-3">
                        <span className="flex items-start gap-2">
                            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />{fehler}
                        </span>
                        <button onClick={() => setFehler(null)} className="shrink-0 hover:bg-red-100 rounded p-1">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                )}

                {/* Faellige Wiedervorlagen — bewusst HIER und nicht im globalen
                    Aufgabensystem (ERP-Plan 4; Begruendung in
                    leadService.listOffeneWiedervorlagen). */}
                {faellige.length > 0 && (
                    <section className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                        <h3 className="font-bold text-amber-900 text-sm flex items-center gap-2 mb-2">
                            <CalendarClock className="w-4 h-4" />
                            {faellige.length} fällige Wiedervorlage{faellige.length === 1 ? '' : 'n'}
                        </h3>
                        <ul className="space-y-1.5">
                            {faellige.slice(0, 8).map((a) => (
                                <li key={a.id} className="flex items-center justify-between gap-3 text-sm">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            const l = leads.find((x) => x.id === a.leadId);
                                            if (l) setAusgewaehlt(l);
                                        }}
                                        className="text-left text-amber-900 hover:underline truncate"
                                    >
                                        <span className="font-bold">{a.leadName || 'Lead'}</span>
                                        {a.betreff && <span> — {a.betreff}</span>}
                                        <span className="text-xs text-amber-700"> (fällig {datumDe(a.faelligAm)})</span>
                                    </button>
                                    <button
                                        onClick={() => void wiedervorlageErledigen(a)}
                                        title="Als erledigt markieren"
                                        className="shrink-0 p-1.5 rounded-lg border border-amber-300 text-amber-700 hover:bg-amber-100"
                                    >
                                        <Check className="w-4 h-4" />
                                    </button>
                                </li>
                            ))}
                        </ul>
                        {faellige.length > 8 && (
                            <p className="text-xs text-amber-700 mt-2">und {faellige.length - 8} weitere…</p>
                        )}
                    </section>
                )}

                {/* Inhalt */}
                {laedt ? (
                    <div className="py-24 text-center text-slate-400 bg-white rounded-3xl border border-slate-200">
                        <Loader2 className="w-8 h-8 animate-spin mx-auto mb-3 text-blue-500" />
                        <p className="text-sm font-medium">Leads werden geladen…</p>
                    </div>
                ) : ansicht === 'board' ? (
                    <LeadsBoard
                        leads={leads}
                        activeProjects={activeProjects}
                        onSelect={setAusgewaehlt}
                        onStatusChange={(l, to) => void statusWechseln(l, to)}
                    />
                ) : (
                    <LeadsListView
                        leads={leads}
                        activeProjects={activeProjects}
                        onSelect={setAusgewaehlt}
                    />
                )}

                {/* Neuanlage */}
                {anlegenOffen && (
                    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                        <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl animate-scale-in">
                            <h3 className="font-bold text-lg text-slate-800 mb-1">Neuer Lead</h3>
                            <p className="text-sm text-slate-600 mb-3">
                                Firmenname genügt — alles Weitere lässt sich im Detail nachtragen.
                            </p>
                            <input
                                autoFocus
                                value={neuerName}
                                onChange={(e) => setNeuerName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && neuerName.trim() && !lege) void anlegen(); }}
                                placeholder="Firmenname eingeben…"
                                className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm font-bold outline-none focus:ring-4 focus:ring-blue-50 focus:border-blue-500"
                            />
                            <div className="flex justify-end gap-3 mt-4">
                                <Button variant="ghost" onClick={() => setAnlegenOffen(false)} disabled={lege}>Abbrechen</Button>
                                <Button
                                    variant="primary"
                                    onClick={() => void anlegen()}
                                    disabled={!neuerName.trim() || lege}
                                    icon={lege ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                                >
                                    Anlegen
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Verloren-Grund aus dem Board */}
                {verlorenFuer && (
                    <div className="fixed inset-0 z-[100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
                        <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl animate-scale-in">
                            <h3 className="font-bold text-lg text-slate-800 mb-1">
                                „{verlorenFuer.name}" verloren geben
                            </h3>
                            <p className="text-sm text-slate-600 mb-3">
                                Der Grund ist Pflicht — ohne ihn lässt sich später nicht auswerten,
                                woran Angebote scheitern.
                            </p>
                            <textarea
                                autoFocus
                                value={grund}
                                onChange={(e) => setGrund(e.target.value)}
                                rows={3}
                                placeholder="z. B. Preis zu hoch, koordiniert intern, kein Bedarf"
                                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-500/20 resize-y"
                            />
                            <div className="flex justify-end gap-3 mt-4">
                                <Button variant="ghost" onClick={() => setVerlorenFuer(null)}>Abbrechen</Button>
                                <Button
                                    variant="primary"
                                    onClick={() => void verlorenBestaetigen()}
                                    disabled={!grund.trim()}
                                    icon={<Ban className="w-4 h-4" />}
                                >
                                    Verloren geben
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {importOffen && (
                    <LeadImportDialog
                        onClose={() => setImportOffen(false)}
                        onImported={() => void laden()}
                        onReviewRequested={() => { setImportOffen(false); setReviewOffen(true); }}
                    />
                )}

                {reviewOffen && (
                    <LeadMatchReviewModal
                        onClose={() => setReviewOffen(false)}
                        onResolved={() => void laden()}
                    />
                )}
            </div>
        </div>
    );
};
